/**
 * Shared formatting helpers for the Show Detail Page — ported from the
 * reference `theatre-detail-page.js` (`formatDateRange`, `formatRuntime`,
 * `formatDescription`) per `DOCS/shows/detail/brd-backend.md` §3/§4/§2.
 * Kept in one place so `ShowDetailsCard`/`ScheduleTab`/etc. share exactly
 * the same rules rather than each re-deriving them.
 */

/** brd-backend.md §3 — "DD Month YYYY", with 3 special cases. Returns null
 *  (hide the whole row) when there's no start date at all. Formats in
 *  explicit UTC (`timeZone: 'UTC'`) — the stored ISO dates are UTC-anchored
 *  calendar days, so the formatter must read them back the same way
 *  regardless of which timezone the rendering runtime happens to be in. */
export function formatDateRange(startDate: string | null, endDate: string | null): string | null {
  if (!startDate) return null;
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' };
  const start = new Date(startDate).toLocaleDateString('en-GB', opts);
  if (!endDate) return `Starting ${start} — ongoing`;
  const end = new Date(endDate).toLocaleDateString('en-GB', opts);
  if (start === end) return `${start} (One-day event)`;
  return `${start} — ${end}`;
}

/** brd-backend.md §4 — "HHh MMm" + interval note, with TBC/Part-N literal
 *  passthrough. Returns null (hide the row) only when truly unparseable. */
export function formatRuntime(runningTime: string | null): { runtime: string; interval: string } | null {
  if (!runningTime || typeof runningTime !== 'string') return null;
  const raw = runningTime.trim();
  if (!raw) return null;

  if (/^tbc$|to\s+be\s+confirm/i.test(raw)) return { runtime: raw, interval: '' };
  if (/part\s+(one|two|three|\d)/i.test(raw)) return { runtime: raw, interval: '' };

  const hoursMatch = raw.match(/(\d+)\s*(?:hr|hrs|h|hour|hours)/i);
  const minutesMatch = raw.match(/(\d+)\s*(?:min|mins|m|minute|minutes)/i);
  const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : NaN;
  const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
  if (Number.isNaN(hours) && Number.isNaN(minutes)) return null;
  if (minutes > 59) return null;

  const interval = /inc(l|lud|luding)?\.?\s*interval/i.test(raw)
    ? 'Includes interval'
    : /no\s+interval/i.test(raw)
      ? 'No interval'
      : '';

  const safeHours = Number.isNaN(hours) ? 0 : hours;
  const runtime = minutes === 0 ? `${safeHours} hr` : `${safeHours} hr ${minutes} min`;
  return { runtime, interval };
}

/** brd-backend.md §2 — strip disallowed characters (only letters, numbers,
 *  spaces, commas, apostrophes, periods survive), collapse whitespace, then
 *  enforce 10–500-char bounds. Returns null when the cleaned text is too
 *  short (or empty) to count as a real description. */
export function cleanDescription(text: string | null, maxLength: number): { full: string; short: string; isLong: boolean } | null {
  if (!text) return null;
  const clean = text
    .replace(/[^a-zA-Z0-9 ,.']/g, '')
    .replace(/(\r\n|\n|\r)/gm, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean.length < 10) return null;
  const isLong = clean.length > maxLength;
  return { full: clean, short: isLong ? clean.slice(0, maxLength) : clean, isLong };
}

/** Formats a Date as venue-local "2:30 pm" (matches `buildScheduleHTML()`).
 *  Reads back via UTC getters — the showtime is baked into the ISO string's
 *  UTC hour/minute components (LTD's real `Performances[].PerformanceDate`
 *  carries a fixed venue-local wall-clock time), which must render
 *  identically regardless of the viewer's own timezone (and must match
 *  between SSR and client hydration, which run in different processes/
 *  timezones). */
export function formatShowtime(date: Date): string {
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

export type AvailabilityStatus = 'good' | 'limited' | 'soldout';

export interface MockPerformance {
  performanceId: number;
  dateTime: string;
  availableTickets: number;
  minPrice: number;
  isBookable: boolean;
}

/** yyyy-mm-dd in UTC — used as the calendar's per-day key throughout, so a
 *  performance's venue-local calendar date always matches the calendar
 *  cell it's grouped under, regardless of which timezone reads it back
 *  (server render vs. browser hydration). The stored ISO instants (from
 *  LTD's real `Performances[].PerformanceDate`) are UTC-anchored
 *  specifically so this read-back is deterministic everywhere. */
export function dateKey(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** brd-backend.md §5.2 originally specified Good/Limited/Sold-Out as a PERCENTAGE of venue
 *  capacity — but LTD's real `Events/{id}/Performances` response carries no total-capacity field
 *  at all (only `TotalAvailableTickesCount`), so this uses absolute-count thresholds instead:
 *  0 → sold out, <10 → limited, else good. An adaptation forced by the real data shape, not an
 *  arbitrary change. */
export function performanceStatus(p: MockPerformance): AvailabilityStatus {
  if (p.availableTickets <= 0) return 'soldout';
  return p.availableTickets < 10 ? 'limited' : 'good';
}

/** Groups performances by local calendar date. */
export function groupPerformancesByDate(performances: MockPerformance[]): Map<string, MockPerformance[]> {
  const map = new Map<string, MockPerformance[]>();
  performances.forEach((p) => {
    const key = dateKey(new Date(p.dateTime));
    const list = map.get(key) || [];
    list.push(p);
    map.set(key, list);
  });
  return map;
}

const STATUS_RANK: Record<AvailabilityStatus, number> = { good: 0, limited: 1, soldout: 2 };

/** brd-backend.md §5.2 — up to 2 marker dots per date, Good's mark always
 *  in the first position (the source's own wording: "if its first show,
 *  show in the first position; otherwise, display it in the second
 *  position" — generalised here as a stable sort by status rank, capped at
 *  2 markers since no source example shows more than 2 shows/day). */
export function dateMarkers(dayPerformances: MockPerformance[]): AvailabilityStatus[] {
  return dayPerformances
    .map(performanceStatus)
    .sort((a, b) => STATUS_RANK[a] - STATUS_RANK[b])
    .slice(0, 2);
}

/** brd-backend.md §5.3 — today's date if it has any performance, else the
 *  next date (chronologically) that has one. Returns null if there are no
 *  future/today performances at all. `todayKey` MUST be resolved once by the
 *  server (`page.tsx`) and threaded down as a prop — computing `new Date()`
 *  independently inside a Client Component causes a hydration mismatch,
 *  since the server process and the browser can run in different
 *  timezones and disagree on what calendar day "today" is. */
export function getDefaultSelectedDate(performances: MockPerformance[], todayKey: string): string | null {
  const byDate = groupPerformancesByDate(performances);
  if (byDate.has(todayKey)) return todayKey;
  const futureDates = [...byDate.keys()].filter((k) => k >= todayKey).sort();
  return futureDates[0] ?? null;
}

/** Lowest bookable price across all performances — feeds the mobile sticky
 *  "Find Tickets" bar's price label (`.show-minimum-price` in the reference).
 *  Returns null when nothing is bookable at all. */
export function minBookablePrice(performances: MockPerformance[]): number | null {
  const bookable = performances.filter((p) => p.isBookable && p.availableTickets > 0);
  if (bookable.length === 0) return null;
  return Math.min(...bookable.map((p) => p.minPrice));
}

export interface ScheduleDay {
  dayName: string;
  dateLabel: string;
  matinee: string | null;
  evening: string | null;
}

/**
 * Rolling 7-day-from-today schedule window — `frd.md` §7 item 5: the real
 * shipped `buildScheduleHTML()` uses a rolling window starting from today's
 * weekday, NOT the PDF mockup's fixed Monday-first table; this port follows
 * the real shipped behavior. A performance before 17:00 counts as a
 * matinee, 17:00+ as an evening (same cutoff as the reference).
 * `hasAny` drives the whole-tab hide rule (brd-backend.md §6/FR-TAB-03).
 *
 * `todayKey` MUST be resolved once by the server and threaded down (see
 * `getDefaultSelectedDate`'s doc comment) — this is called from `ScheduleTab`
 * and `ShowDetailTabs`, both reachable from the `'use client'` tab tree, so
 * an internal `new Date()` here previously caused a real SSR/hydration
 * mismatch (server and browser disagreeing on "today" across a timezone
 * boundary) that surfaced as React's "text didn't match" hydration error.
 */
export function buildWeeklySchedule(
  performances: Array<{ dateTime: string }>,
  todayKey: string,
): { days: ScheduleDay[]; hasAny: boolean } {
  const allDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const today = new Date(`${todayKey}T00:00:00Z`);
  const todayIndex = today.getUTCDay();

  const days: ScheduleDay[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setUTCDate(today.getUTCDate() + i);
    days.push({
      dayName: allDayNames[(todayIndex + i) % 7],
      dateLabel: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' }),
      matinee: null,
      evening: null,
    });
  }

  performances.forEach((p) => {
    const date = new Date(p.dateTime);
    const dayStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const offset = Math.round((dayStart.getTime() - today.getTime()) / 86400000);
    if (offset < 0 || offset > 6) return;
    const time = formatShowtime(date);
    if (date.getUTCHours() < 17) days[offset].matinee = time;
    else days[offset].evening = time;
  });

  const hasAny = days.some((d) => d.matinee || d.evening);
  return { days, hasAny };
}
